import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircleIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  PlayCircleIcon,
  QueueListIcon
} from '@heroicons/react/24/outline';
import { Spinner } from '@oysterjs/ui/spinner';
import { MountableSlideout } from '@oysterjs/ui/slideout';

import { TriggerAuditLevel, TriggerAuditType, useGetTriggerEventQuery } from '~/api/graphql';
import {
  EventStateBadge,
  EVENTS_PATH,
  eventSummary,
  EventTypeBadge,
  formatEventTimestamp,
  formatMatchCount,
  matchedRunCount,
  RunStateBadge
} from './trigger-utils';

type HeroIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

/** Hidden entries are internal bookkeeping; errors are shown regardless of type. */
const AUDIT_ENTRIES: Record<TriggerAuditType, { title: string; icon: HeroIcon; visible: boolean }> =
  {
    [TriggerAuditType.EventReceived]: {
      title: 'Event received',
      icon: QueueListIcon,
      visible: true
    },
    [TriggerAuditType.EvaluationCompleted]: {
      title: 'Processing complete',
      icon: CheckCircleIcon,
      visible: true
    },
    [TriggerAuditType.RunQueued]: {
      title: 'Workspace queued',
      icon: PlayCircleIcon,
      visible: true
    },
    [TriggerAuditType.TargetLaunched]: {
      title: 'Workspace started',
      icon: CheckCircleIcon,
      visible: true
    },
    [TriggerAuditType.Failed]: {
      title: 'Failed',
      icon: ExclamationCircleIcon,
      visible: true
    },
    [TriggerAuditType.CapacityAcquired]: {
      title: 'Capacity acquired',
      icon: QueueListIcon,
      visible: false
    },
    [TriggerAuditType.CapacityReleased]: {
      title: 'Capacity released',
      icon: QueueListIcon,
      visible: false
    }
  };

const TimelineItem = (props: {
  icon: HeroIcon;
  title: string;
  description: React.ReactNode;
  complete?: boolean;
  last?: boolean;
}) => (
  <li className="relative flex gap-4 pb-6 last:pb-0">
    {!props.last ? (
      <div className="absolute left-[0.6875rem] top-6 h-full w-px bg-neutral-200 dark:bg-neutral-700" />
    ) : null}
    <div className="relative z-10 flex size-6 flex-none items-center justify-center rounded-full bg-white dark:bg-neutral-800">
      <props.icon
        className={
          props.complete
            ? 'size-6 text-green-500 dark:text-green-400'
            : 'size-6 text-neutral-400 dark:text-neutral-500'
        }
      />
    </div>
    <div className="min-w-0 pt-0.5">
      <p className="m-0 text-sm font-semibold text-neutral-900 dark:text-white">{props.title}</p>
      <div className="mt-1 text-sm/6 text-neutral-500 dark:text-neutral-400">
        {props.description}
      </div>
    </div>
  </li>
);

const EventDetailContent = ({ eventId }: { eventId: string }) => {
  const { data, loading, error } = useGetTriggerEventQuery({
    variables: { id: eventId },
    skip: !eventId
  });
  const event = data?.triggerEvent;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="size-8" color="neutral" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Event not found</p>
      </div>
    );
  }

  const matchCount = matchedRunCount(event);
  const timeline = event.auditLogs.filter(
    (entry) => AUDIT_ENTRIES[entry.type].visible || entry.level === TriggerAuditLevel.Error
  );

  return (
    <div className="space-y-6 pb-8">
      <div>
        <h3 className="m-0 text-lg font-medium text-neutral-900 dark:text-white">
          {eventSummary(event)}
        </h3>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <EventStateBadge state={event.state} />
          <EventTypeBadge eventType={event.eventType} />
        </div>
        <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
          {[formatEventTimestamp(event.receivedAt), formatMatchCount(matchCount)]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>

      <div>
        <h4 className="m-0 text-sm font-semibold text-neutral-900 dark:text-white">
          What happened
        </h4>
        {timeline.length === 0 ? (
          <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">No events yet.</p>
        ) : (
          <ol className="mt-4">
            {timeline.map((entry, index) => {
              const isError = entry.level === TriggerAuditLevel.Error;
              const { title, icon } = AUDIT_ENTRIES[entry.type];
              return (
                <TimelineItem
                  key={entry.id}
                  icon={isError ? ExclamationCircleIcon : icon}
                  complete={!isError}
                  last={index === timeline.length - 1}
                  title={title}
                  description={
                    <>
                      <p className="m-0 text-sm">{entry.message}</p>
                      <p className="m-0 mt-1 text-xs text-neutral-400 dark:text-neutral-500">
                        {formatEventTimestamp(entry.createdAt)}
                      </p>
                    </>
                  }
                />
              );
            })}
          </ol>
        )}
      </div>

      <div>
        <h4 className="m-0 text-sm font-semibold text-neutral-900 dark:text-white">Workspaces</h4>
        {event.runs.length === 0 ? (
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
            No workspaces launched yet.
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {event.runs.map((run) => {
              const content = (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <p className="m-0 min-w-0 truncate text-sm font-medium text-neutral-900 dark:text-white">
                      {run.triggerDefinition.name}
                    </p>
                    <div className="flex flex-none items-center gap-2">
                      <RunStateBadge state={run.state} />
                      {run.executionId ? (
                        <ChevronRightIcon className="size-4 text-neutral-400 dark:text-neutral-500" />
                      ) : null}
                    </div>
                  </div>
                  {run.attemptCount > 1 ? (
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      Attempt {run.attemptCount}
                    </p>
                  ) : null}
                  {run.error ? (
                    <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                      {run.error.message}
                    </p>
                  ) : null}
                </>
              );
              return (
                <li key={run.id}>
                  {run.executionId ? (
                    <Link
                      to={`/app/workspaces/${run.executionId}`}
                      className="block rounded-md border border-neutral-200 px-3 py-3 hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-700 dark:hover:border-neutral-600 dark:hover:bg-white/5"
                    >
                      {content}
                    </Link>
                  ) : (
                    <div className="rounded-md border border-neutral-200 px-3 py-3 dark:border-neutral-700">
                      {content}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {event.evaluationSummary && matchCount !== event.evaluationSummary.candidateCount ? (
        <div>
          <h4 className="m-0 text-sm font-semibold text-neutral-900 dark:text-white">
            Matched triggers
          </h4>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
            {matchCount} of {event.evaluationSummary.candidateCount} triggers matched this event
          </p>
        </div>
      ) : null}

      {event.error ? (
        <div>
          <h4 className="m-0 text-sm font-semibold text-neutral-900 dark:text-white">Error</h4>
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{event.error.message}</p>
        </div>
      ) : null}
    </div>
  );
};

export const EventDetailPage = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  return (
    <MountableSlideout open={!!eventId} onClose={() => navigate(EVENTS_PATH)} title="Event details">
      {eventId ? <EventDetailContent eventId={eventId} /> : null}
    </MountableSlideout>
  );
};
